// Device-level user settings: theme, accent, language, celebrations, and the
// leader's remembered area. Persisted per device.
//
// The home-mode and pod-graphic knobs are gone: the timeline they switched
// between is now the Pods tab, and the hero card they styled has been replaced.
// A setting whose screens no longer exist is a lever attached to nothing.

import type { StateCreator } from 'zustand';
import type { AccentName, ThemeMode } from '@/theme/palette';
import type { Lang } from '@/i18n/strings';

export interface SettingsSlice {
  userTheme: ThemeMode;
  userAccent: AccentName;
  language: Lang;
  /** Date key ('YYYY-MM-DD') of the last milestone shown — one per day. */
  milestoneShownOn: string | null;
  /** One-time consent for browser speech recognition (cloud-processed). */
  speechConsent: boolean;
  /**
   * Date key ('YYYY-MM-DD') the install invitation was last dismissed on, or
   * null if it never was. A date rather than a flag so it can lapse — see
   * INSTALL_REPROMPT_DAYS.
   */
  installDismissedOn: string | null;
  /**
   * The leader's department filter, remembered per device.
   *
   * Null is every area. Remembered because a leader looks after the same part
   * of the floor every day, and re-picking it each morning is a tax on the one
   * screen that is supposed to be glanceable.
   */
  leaderDepartmentId: number | null;

  /**
   * The last few ISWs opened, most recent first — the library's "Recently
   * opened" list. Kept on the device, which is the operator's own.
   */
  iswRecent: string[];

  setTheme: (v: ThemeMode) => void;
  setAccent: (v: AccentName) => void;
  setLanguage: (v: Lang) => void;
  cycleLanguage: () => void;
  markMilestoneShown: (dateKey: string) => void;
  setSpeechConsent: (v: boolean) => void;
  dismissInstall: (dateKey: string) => void;
  setLeaderDepartment: (id: number | null) => void;
  /** Record that an ISW was opened. Caps the list at three, as the design does. */
  noteIswOpened: (iswId: string) => void;
}

const NEXT_LANG: Record<Lang, Lang> = { AU: '中文', 中文: 'Fil', Fil: 'AU' };

export const createSettingsSlice: StateCreator<SettingsSlice, [], [], SettingsSlice> = (set) => ({
  userTheme: 'light',
  userAccent: 'Interpod Green',
  language: 'AU',
  milestoneShownOn: null,
  speechConsent: false,
  installDismissedOn: null,
  leaderDepartmentId: null,
  iswRecent: [],

  setTheme: (userTheme) => set({ userTheme }),
  setAccent: (userAccent) => set({ userAccent }),
  setLanguage: (language) => set({ language }),
  cycleLanguage: () => set((s) => ({ language: NEXT_LANG[s.language] })),
  markMilestoneShown: (milestoneShownOn) => set({ milestoneShownOn }),
  setSpeechConsent: (speechConsent) => set({ speechConsent }),
  dismissInstall: (installDismissedOn) => set({ installDismissedOn }),
  setLeaderDepartment: (leaderDepartmentId) => set({ leaderDepartmentId }),
  noteIswOpened: (iswId) =>
    set((s) => ({ iswRecent: [iswId, ...s.iswRecent.filter((id) => id !== iswId)].slice(0, 3) })),
});
